import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import { updateSampleWithCollectionData } from '../../../utils/sampleCollectionUtils';
import { createAutomaticSampleInvoice } from '../../../utils/automaticInvoice';
import { generateInvoicePdf } from '../../../pages/accounting/components/InvoiceForm/generateInvoicePdf';
import { Loader2, X, Beaker, ThermometerSun, Droplets, Ruler, PaintBucket, Calendar, Clock, Hash } from 'lucide-react';

interface SampleCollectionModalProps {
  show: boolean;
  onClose: () => void;
  sample: {
    id: string;
    sample_type_name: string;
    requires_temperature: boolean;
    requires_ph: boolean;
    requires_creatinine: boolean;
    requires_hair_length: boolean;
    requires_remaining_hair_length: boolean;
    requires_extraction_site: boolean;
    requires_hair_color: boolean;
    requires_cosmetic_treatment: boolean;
    sample_data: any;
    internal_order_number?: string | null;
  };
  onSampleCollected: () => void;
}

const SampleCollectionModal: React.FC<SampleCollectionModalProps> = ({ 
  show, 
  onClose, 
  sample,
  onSampleCollected
}) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collectionDate, setCollectionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [collectionTime, setCollectionTime] = useState<string>(new Date().toTimeString().slice(0, 5));
  const [sampleData, setSampleData] = useState<Record<string, any>>({});
  const [internalOrderNumber, setInternalOrderNumber] = useState<string>('');
  const [createInvoiceForSample, setCreateInvoiceForSample] = useState(true);
  const [canUseAutomaticInvoiceForSample, setCanUseAutomaticInvoiceForSample] = useState(false);

  const resolveOrganizationIdForSample = async () => {
    // Primärer Weg: Organisation über die sample_details-View auflösen.
    // Diese View enthält organization_id direkt am Probendatensatz.
    const { data: sampleRow, error: sampleRowError } = await supabase
      .from('sample_details')
      .select('organization_id')
      .eq('id', sample.id)
      .maybeSingle();

    if (!sampleRowError && sampleRow?.organization_id) {
      return sampleRow.organization_id;
    }

    // Fallback: Falls die View nicht verfügbar ist oder kein Treffer geliefert wird,
    // ermitteln wir die Organisation über customer_samples -> customers.
    const { data: sampleCustomer, error: sampleCustomerError } = await supabase
      .from('customer_samples')
      .select('customer_id')
      .eq('id', sample.id)
      .maybeSingle();

    if (sampleCustomerError) {
      // Wenn sowohl Primär- als auch Fallback-Abfrage fehlschlagen,
      // geben wir den ursprünglichen View-Fehler bevorzugt zurück.
      throw sampleRowError ?? sampleCustomerError;
    }

    if (!sampleCustomer?.customer_id) {
      return null;
    }

    const { data: customerRow, error: customerRowError } = await supabase
      .from('customers')
      .select('organization_id')
      .eq('id', sampleCustomer.customer_id)
      .maybeSingle();

    if (customerRowError) {
      throw sampleRowError ?? customerRowError;
    }

    return customerRow?.organization_id ?? null;
  };

  const downloadInvoicePdf = async (invoiceId: string, organizationId: string) => {
    // Lädt alle Rechnungsdaten nach Erstellung und startet den direkten PDF-Download.
    const [{ data: invoice, error: invoiceError }, { data: invoiceItems, error: itemsError }, { data: organization, error: organizationError }] = await Promise.all([
      supabase
        .from('invoices')
        .select('*')
        .eq('id', invoiceId)
        .single(),
      supabase
        .from('invoice_items')
        .select('product_name, description, quantity, unit_price, tax_rate, discount_amount, amount')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: true }),
      supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single(),
    ]);

    if (invoiceError) throw invoiceError;
    if (itemsError) throw itemsError;
    if (organizationError) throw organizationError;

    // Holt PDF-Einstellungen kompatibel aus neuer und alter Tabelle, damit der Download
    // auch in Datenbanken ohne `accounting_settings` View funktioniert.
    let accountingSettings: Record<string, unknown> = {};
    const { data: organizationAccountingSettings, error: organizationAccountingSettingsError } = await supabase
      .from('organization_accounting_settings')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (!organizationAccountingSettingsError && organizationAccountingSettings) {
      accountingSettings = organizationAccountingSettings;
    } else {
      const { data: legacyAccountingSettings, error: legacyAccountingSettingsError } = await supabase
        .from('accounting_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (!legacyAccountingSettingsError && legacyAccountingSettings) {
        accountingSettings = {
          ...legacyAccountingSettings,
          // Legacy-Feldname auf den aktuellen Schlüssel normalisieren.
          invoice_price_display_mode:
            (legacyAccountingSettings as any).invoice_price_display_mode ??
            (legacyAccountingSettings as any).price_display_mode,
        };
      }
    }

    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('*')
      .eq('id', invoice.customer_id)
      .maybeSingle();

    if (customerError) throw customerError;

    await generateInvoicePdf({
      invoice,
      invoiceItems: invoiceItems ?? [],
      selectedCustomer: customer,
      organization,
      settings: accountingSettings,
    });
  };
  
  // Initialize form data from sample
  useEffect(() => {
    if (show && sample) {
      setSampleData(sample.sample_data || {});
      // Initialize with current date and time
      setCollectionDate(new Date().toISOString().split('T')[0]);
      setCollectionTime(new Date().toTimeString().slice(0, 5));
      
      // Reset internal order number field to be empty
      setInternalOrderNumber('');

      const loadSettings = async () => {
        if (!user?.id) {
          setCanUseAutomaticInvoiceForSample(false);
          setCreateInvoiceForSample(false);
          return;
        }

        let organizationId: string | null = null;

        try {
          organizationId = await resolveOrganizationIdForSample();
        } catch (resolveOrganizationError) {
          console.error('Error resolving organization for sample invoice setting:', resolveOrganizationError);
        }

        if (!organizationId) {
          setCanUseAutomaticInvoiceForSample(false);
          setCreateInvoiceForSample(false);
          return;
        }

        // Nur das organisationsweite Auto-Rechnungs-Flag für Proben laden.
        const { data: organizationSettings, error: organizationSettingsError } = await supabase
          .from('organizations')
          .select('auto_invoice_on_sample_collection')
          .eq('id', organizationId)
          .single();

        if (organizationSettingsError) {
          setCanUseAutomaticInvoiceForSample(false);
          setCreateInvoiceForSample(false);
          return;
        }

        // Das Buchhaltungs-Feature kommt aus organization_features.
        const { data: organizationFeatures, error: organizationFeaturesError } = await supabase
          .from('organization_features')
          .select('feature_accounting')
          .eq('organization_id', organizationId)
          .maybeSingle();

        if (organizationFeaturesError && organizationFeaturesError.code !== '42P01' && organizationFeaturesError.code !== '42703') {
          setCanUseAutomaticInvoiceForSample(false);
          setCreateInvoiceForSample(false);
          return;
        }

        // Checkbox nur anzeigen, wenn Buchhaltungs-Feature aktiv ist UND der organisationsweite
        // Auto-Rechnungs-Workflow für Proben aktiv ist.
        const hasAccountingFeature = Boolean(organizationFeatures?.feature_accounting);
        const isAutoInvoiceEnabledForSamples = Boolean(organizationSettings?.auto_invoice_on_sample_collection);
        const shouldShowAutomaticInvoiceOption = hasAccountingFeature && isAutoInvoiceEnabledForSamples;

        setCanUseAutomaticInvoiceForSample(shouldShowAutomaticInvoiceOption);
        setCreateInvoiceForSample(shouldShowAutomaticInvoiceOption);
      };

      void loadSettings();
    }
  }, [show, sample, supabase, user?.id]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Generate internal order number if not provided
      const finalOrderNumber = internalOrderNumber.trim() || `${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
      
      // Use the shared utility to update the sample
      const { error } = await updateSampleWithCollectionData(
        supabase,
        sample.id,
        {
          collection_date: collectionDate,
          collection_time: collectionTime,
          collector_name: user?.full_name || user?.id || 'Unbekannt' || 'Unknown',
          collector_id: user?.id,
          internal_order_number: finalOrderNumber
        },
        sampleData
      );

      if (error) throw error;

      const { error: consumptionError } = await supabase
        .rpc('consume_materials_for_sample', { p_sample_id: sample.id });

      // Merkt sich optional die automatisch erzeugte Rechnung, damit der PDF-Download
      // erst nach dem Schließen des Modals ausgelöst werden kann (wichtig für Mobile/Tablet-Workflows).
      let pendingInvoiceDownload: { invoiceId: string; organizationId: string } | null = null;

      if (user?.id && createInvoiceForSample) {
        const organizationId = await resolveOrganizationIdForSample();

        if (organizationId) {
          const automaticInvoiceResult = await createAutomaticSampleInvoice({
            supabase,
            organizationId,
            sampleId: sample.id,
            createdBy: user.id,
            enabled: true,
            collectionMode: sampleData.extraction_site || null,
          });

          if (automaticInvoiceResult.created && automaticInvoiceResult.invoiceId) {
            pendingInvoiceDownload = {
              invoiceId: automaticInvoiceResult.invoiceId,
              organizationId,
            };
          }
        }
      }

      const shouldAutoClose = !consumptionError;

      if (consumptionError) {
        console.error('Error consuming materials for sample:', consumptionError);
        setError('Probe gespeichert, aber Materialbestand konnte nicht aktualisiert werden. Bitte prüfen Sie die Materialverwaltung.');
      }

      // Fetch the updated sample to get the generated order number
      const { data: updatedSample, error: fetchError } = await supabase
        .from('customer_samples')
        .select('internal_order_number')
        .eq('id', sample.id)
        .single();
        
      if (fetchError) throw fetchError;

      // Show the generated order number if it was automatically assigned
      if (updatedSample && updatedSample.internal_order_number && !internalOrderNumber.trim()) {
        setInternalOrderNumber(updatedSample.internal_order_number);
        // Brief delay to show the generated number before closing
        if (shouldAutoClose) {
          setTimeout(() => {
            onSampleCollected();
            onClose();

            // PDF-Download bewusst nach dem Modal-Close starten, damit die laufende
            // Entnahme-/Vertragserstellung auf mobilen Geräten nicht unterbrochen wird.
            if (pendingInvoiceDownload) {
              void setTimeout(() => {
                void downloadInvoicePdf(pendingInvoiceDownload.invoiceId, pendingInvoiceDownload.organizationId).catch((downloadError) => {
                  console.error('Fehler beim PDF-Download der automatischen Rechnung:', downloadError);
                });
              }, 250);
            }
          }, 500);
        } else {
          onSampleCollected();
        }
      } else {
        onSampleCollected();
        if (shouldAutoClose) {
          onClose();

          // PDF-Download bewusst nach dem Modal-Close starten, damit die laufende
          // Entnahme-/Vertragserstellung auf mobilen Geräten nicht unterbrochen wird.
          if (pendingInvoiceDownload) {
            void setTimeout(() => {
              void downloadInvoicePdf(pendingInvoiceDownload.invoiceId, pendingInvoiceDownload.organizationId).catch((downloadError) => {
                console.error('Fehler beim PDF-Download der automatischen Rechnung:', downloadError);
              });
            }, 250);
          }
        }
      }
    } catch (err: any) {
      console.error('Error collecting sample:', err);
      setError(err.message || 'Fehler beim Speichern der Probenentnahme');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field: string, value: any) => {
    setSampleData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Beaker className="h-5 w-5 mr-2 text-primary-500" />
            Probe entnehmen
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-md">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Probenart:</span> {sample.sample_type_name}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              <span className="font-medium">Entnommen von:</span> {user?.full_name || user?.id || 'Unbekannt'}
            </p>
          </div>

          {/* Internal Order Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Hash className="h-4 w-4 mr-1 text-gray-400" />
              Interne Auftragsnummer
            </label>
            <input
              type="text"
              value={internalOrderNumber}
              onChange={(e) => setInternalOrderNumber(e.target.value)}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              placeholder="z.B. 2025-1234"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Wenn leer, wird automatisch eine Nummer generiert
            </p>
          </div>
          
          {/* Date and Time Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
              Entnahmedatum und -zeit
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="relative">
                <input
                  type="date"
                  value={collectionDate}
                  onChange={(e) => setCollectionDate(e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="time"
                  value={collectionTime}
                  onChange={(e) => setCollectionTime(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                />
              </div>
            </div>
          </div>

          {canUseAutomaticInvoiceForSample && (
            <div className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
            <label className="inline-flex items-start gap-3">
              <input
                type="checkbox"
                checked={createInvoiceForSample}
                onChange={(event) => setCreateInvoiceForSample(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  Automatische Rechnung für diese Probe erstellen
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Diese Einstellung gilt nur für die aktuelle Entnahme und kann hier deaktiviert werden.
                </span>
              </span>
            </label>
          </div>
          )}
          
          {/* Urine sample fields */}
          {(sample.sample_type_name === 'Urin' || sample.sample_type_name === 'Schnelltest' || sample.requires_temperature) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <ThermometerSun className="h-4 w-4 mr-1 text-gray-400" />
                Temperatur (°C) Normalbereich (32°C - 39°C)
              </label>
              <input
                type="number"
                step="0.1"
                value={sampleData.temperature || ''}
                onChange={(e) => handleInputChange('temperature', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                placeholder="36.5"
              />
            </div>
          )}
          
          {(sample.sample_type_name === 'Urin' || sample.sample_type_name === 'Schnelltest' || sample.requires_ph) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Droplets className="h-4 w-4 mr-1 text-gray-400" /> 
                pH-Wert
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="14"
                value={sampleData.ph || ''}
                onChange={(e) => handleInputChange('ph', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                placeholder="7.0"
              />
            </div>
          )}

          
          {sample.requires_hair_length && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Ruler className="h-4 w-4 mr-1 text-gray-400" />
                Gesamte Haarlänge (cm)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={sampleData.hair_length || ''}
                onChange={(e) => handleInputChange('hair_length', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                placeholder="5.0"
              />
            </div>
          )}
          
          {sample.requires_remaining_hair_length && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Ruler className="h-4 w-4 mr-1 text-gray-400" />
                Verbleibende Haarlänge (mm)
              </label>
<input
  type="number"
  step="0.1"
  min="0"
  value={sampleData.remaining_hair_length ?? ''}
  onChange={(e) => {
    let value = e.target.value.replace(',', '.'); // Komma durch Punkt ersetzen

    if (value === '') {
      value = 0;
    } else {
      value = parseFloat(value);
      if (!isNaN(value)) {
        value = Math.round(value * 10) / 10;
      }
    }

    handleInputChange('remaining_hair_length', value);
  }}
  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
  placeholder="3.0"
/>

            </div>
          )}
          
          {sample.requires_extraction_site && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entnahmestelle
              </label>
              <div className="flex items-center space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="Kopf"
                    checked={sampleData.extraction_site === 'Kopf'}
                    onChange={() => handleInputChange('extraction_site', 'Kopf')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Kopf</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="Andere"
                    checked={sampleData.extraction_site === 'Andere'}
                    onChange={() => handleInputChange('extraction_site', 'Andere')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Andere</span>
                </label>
              </div>
              
              {sampleData.extraction_site === 'Andere' && (
                <input
                  type="text"
                  value={sampleData.extraction_site_andere || ''}
                  onChange={(e) => handleInputChange('extraction_site_andere', e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="Bitte angeben"
                />
              )}
            </div>
          )}
          
          {sample.requires_hair_color && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <PaintBucket className="h-4 w-4 mr-1 text-gray-400" />
                Haarfarbe
              </label>
              <input
                type="text"
                value={sampleData.hair_color || ''}
                onChange={(e) => handleInputChange('hair_color', e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                placeholder="z.B. Braun, Blond, Schwarz"
              />
            </div>
          )}
          
          {sample.requires_cosmetic_treatment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Chemische Behandlung
              </label>
              <div className="flex items-center space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="Keine"
                    checked={sampleData.cosmetic_treatment === 'Keine'}
                    onChange={() => handleInputChange('cosmetic_treatment', 'Keine')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Keine</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="Behandelt"
                    checked={sampleData.cosmetic_treatment === 'Behandelt'}
                    onChange={() => handleInputChange('cosmetic_treatment', 'Behandelt')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Behandelt</span>
                </label>
              </div>
              
              {sampleData.cosmetic_treatment === 'Behandelt' && (
                <input
                  type="text"
                  value={sampleData.cosmetic_treatment_behandelt || ''}
                  onChange={(e) => handleInputChange('cosmetic_treatment_behandelt', e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="Art der Behandlung"
                />
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Wird gespeichert...
                </>
              ) : (
                'Probe entnehmen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SampleCollectionModal;
